import React from 'react';

import { Field } from '../../../design-system/components/desktop/primitives';
import { useViewport } from '../../../design-system/hooks/useViewport';
import { TrackButton, TrackRating, TrackTextarea } from './tracking.parts';

/**
 * The review form body. Pure: every input is a prop, every output a callback.
 *
 * The payload `submit_rating` accepts is exactly `{rating, review, order_id}`
 * and nothing else, so nothing else is on this form. The container owns the
 * mutation, the toast and the close.
 *
 * The stars are the library `Rating` in input mode, which is real radio inputs
 * with the fill, the hover run and the keyboard handled in CSS. The old
 * `RateUs` component was five hand-drawn SVGs with a hardcoded `#fbbf24`, a
 * `.dark-theme .star-empty` rule and no keyboard access at all; it is gone.
 * Stars are ink, never yellow.
 *
 * The value is also spelled out beside them, because a rating carried only by
 * a count of filled shapes is invisible to anyone who cannot see the fill.
 */
export function ReviewFormView({
  orderId,
  rating = 0,
  onRatingChange = () => {},
  review = '',
  onReviewChange = () => {},
  onSubmit = () => {},
  onCancel,
  loading = false,
}) {
  const { isDesktop } = useViewport();

  function handleSubmit(event) {
    event.preventDefault();
    onSubmit({ rating, review });
  }

  const stars = (
    <div className="dsa-track__rate">
      <span className="dsa-track__rate-label" id="review-rate-label">
        Your rating
      </span>
      <TrackRating value={rating} onChange={onRatingChange} label="Your rating, 1 to 5" />
      <span className="dsa-track__rate-value">
        {rating ? `${rating} of 5` : 'Not rated'}
      </span>
    </div>
  );

  const description = (
    <TrackTextarea
      rows={5}
      value={review}
      onChange={(event) => onReviewChange(event.target.value)}
      placeholder="How was the card, the packaging and the wait?"
      disabled={loading}
      aria-label="Review description"
    />
  );

  return (
    <form className="dsa-track__form" onSubmit={handleSubmit} noValidate>
      {stars}

      {isDesktop ? (
        <Field label="Description" optional>
          {description}
        </Field>
      ) : (
        <label className="ds-field">
          <span className="ds-field__label">Description</span>
          <span className="ds-field__control">{description}</span>
        </label>
      )}

      <div className="dsa-track__form-actions">
        {onCancel ? (
          <TrackButton variant="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </TrackButton>
        ) : null}
        <TrackButton type="submit" loading={loading} disabled={loading || !rating}>
          {loading ? 'Submitting' : 'Submit review'}
        </TrackButton>
      </div>

      {/* Carried so the container's payload and the copy agree about which
          order is being reviewed. Never rendered as a price or a courier. */}
      {orderId ? (
        <input type="hidden" name="order_id" value={orderId} readOnly />
      ) : null}
    </form>
  );
}

export default ReviewFormView;
